import dayjs from "dayjs";
import type { VideoComment } from "./commentsPdfGenerator";
import { toDraft } from "../components/Mentions/MentionText";

const BASE_HEADERS = ["Version", "Date", "Time", "Sender", "Comment", "Status"];

export interface CommentGroup {
    videoName: string;
    projectTitle?: string;
    comments: VideoComment[];
}

const sender = (comment: VideoComment): string =>
    comment.guest_name || comment.user?.displayName || "Unknown";

const status = (comment: VideoComment): string => (comment.deleted ? "Deleted" : "Active");

const escapeCell = (value: unknown): string => {
    const raw = value === null || value === undefined ? "" : String(value);
    const guarded = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
    return `"${guarded.replace(/"/g, '""')}"`;
};

const createdAt = (comment: VideoComment) => new Date(comment.created_at ?? 0).getTime();

const versionOf = (comment: VideoComment): number =>
    typeof comment.file?.version === "number" ? comment.file.version : Infinity;

export interface CsvScope {
    withVideo?: boolean;
    withProject?: boolean;
}

export const commentsToCsv = (groups: CommentGroup[], scope: CsvScope = {}): string => {
    const { withVideo = false, withProject = false } = scope;
    const headers = [
        ...(withProject ? ["Project"] : []),
        ...(withVideo ? ["Video"] : []),
        ...BASE_HEADERS,
    ];

    // Grouped per video, then by version (oldest first), then by time posted.
    const rows = groups
        .flatMap((group, groupIndex) =>
            group.comments.map((comment) => ({ group, groupIndex, comment })),
        )
        .sort(
            (a, b) =>
                a.groupIndex - b.groupIndex ||
                versionOf(a.comment) - versionOf(b.comment) ||
                createdAt(a.comment) - createdAt(b.comment),
        )
        .map(({ group, comment }) => {
            const created = comment.created_at ? dayjs(comment.created_at) : null;
            return [
                ...(withProject ? [group.projectTitle ?? ""] : []),
                ...(withVideo ? [group.videoName] : []),
                typeof comment.file?.version === "number" ? `v${comment.file.version}` : "",
                created ? created.format("MM/DD/YYYY") : "",
                created ? created.format("h:mm A") : "",
                sender(comment),
                // Mentions are stored as @[Name](userid); the file shows @Name.
                toDraft(comment.message ?? ""),
                status(comment),
            ]
                .map(escapeCell)
                .join(",");
        });

    return `\uFEFF${[headers.join(","), ...rows].join("\r\n")}`;
};

export const commentsCsvBlob = (groups: CommentGroup[], scope: CsvScope = {}): Blob =>
    new Blob([commentsToCsv(groups, scope)], { type: "text/csv;charset=utf-8;" });
